<template>
	<v-form v-model="formValid">
		<page :title="report.type.report_type + ' | ' + (report.properties.length > 1 ? report.properties.length + ' Properties' : report.properties[0].full_address)" :breadcrumbs="breadcrumbs">
			<template #actions>
				<v-menu
					v-model="statusMenu"
					offset-y
					:nudge-width="200"
					:close-on-content-click="false">
					<template #activator="{ on, attrs }">
						<fieldset class="mt-n2 mb-2">
							<legend>Report Status</legend>
							<v-btn
								:color="report.status.color"
								:dark="isDarkColor(report.status.color)"
								small
								min-width="200"
								elevation="0"
								v-bind="attrs"
								v-on="on">
								{{ report.status.name }}
							</v-btn>
						</fieldset>
					</template>
					<v-card v-if="length(statuses)">
						<v-card-text>
							<v-autocomplete
								v-model="statusId"
								:items="statuses"
								placeholder="Change Status"
								item-text="name"
								item-value="id"
								filled
								outlined
								clearable
								hide-details>
								<template #selection="data">
									<v-icon left :color="data.item.color">mdi-circle</v-icon>
									<span>{{ data.item.name }}</span>
								</template>
								<template #item="data">
									<v-icon left :color="data.item.color">mdi-circle</v-icon>
									<span>{{ data.item.name }}</span>
								</template>
							</v-autocomplete>
							<v-btn color="primary"
								class="mt-3"
								:disabled="! statusId || statusId === report.status.id || form.isDirty"
								@click="updateStatus">
								Change Status
							</v-btn>
						</v-card-text>
						<v-card-actions v-if="form.isDirty">
							<v-alert color="red" outlined dense class="ma-0"><strong>Unsaved changes!</strong> Save before changing the status.</v-alert>
						</v-card-actions>
					</v-card>
				</v-menu>
			</template>
			<v-row class="mb-2">
				<v-col class="pt-0 mt-n3">
					<Link color="secondary"
						:href="route('projects.show', project)"
						class="mr-2 v-btn v-btn--is-elevated v-btn--has-bg v-btn--rounded theme--light v-size--x-small secondary">
						<v-icon left color="white">mdi-arrow-left-bold</v-icon>
						Back to Project
					</Link>
					<v-btn
						v-if="report.properties.length === 1"
						color="red darken-1"
						x-small
						rounded
						class="white--text"
						:href="route('properties.data', report.properties[0])"
						target="_blank">
						<v-icon left>mdi-open-in-new</v-icon>
						Edit Data
					</v-btn>
				</v-col>
				<v-col class="pt-0 mt-n3 ml-auto text-right">
					<v-btn color="success"
						small
						class="mr-2"
						:loading="saving"
						:disabled="!form.isDirty"
						@click="save(false)">
						<v-icon left>mdi-check-circle</v-icon>
						Save Report
					</v-btn>
					<v-btn color="warning"
						small
						class="mr-2"
						:loading="downloading"
						@click="save(true)">
						<v-icon left>mdi-download-circle</v-icon>
						Download
					</v-btn>
					<v-dialog
						v-model="dialog"
						persistent
						max-width="350"
						transition="scroll-x-reverse-transition">
						<template #activator="{ on, attr }">
							<v-btn color="secondary"
								small
								class="mr-2"
								v-bind="attr"
								v-on="on">
								<v-icon left>mdi-refresh</v-icon>
								Regenerate
							</v-btn>
						</template>
						<v-card>
							<v-card-title class="text-h5">
								Are you sure?
							</v-card-title>
							<v-card-text class="pt-5" style="font-size: 105%">
								This will overwrite the finalized report. It cannot be undone.
							</v-card-text>
							<v-divider />
							<v-card-actions>
								<v-spacer></v-spacer>
								<v-btn
									color="gray lighten-2"
									text
									small
									class="grey--text text--darken-1"
									@click="dialog = false">
									Cancel
								</v-btn>
								<v-btn
									class="secondary"
									dark
									depressed
									small
									:loading="submitted"
									@click="regenerate">
									<v-progress-circular
										v-if="submitted"
										indeterminate
										class="mr-2"
										size="20"
										color="white">
									</v-progress-circular>
									<span v-if="!submitted">Regenerate</span>
									<span v-else>Regenerating</span>
								</v-btn>
							</v-card-actions>
						</v-card>
					</v-dialog>
				</v-col>
			</v-row>
			<v-sheet id="project-summary"
				rounded
				class="mb-4">
				<v-card color="#125B92" elevation="0" class="rounded">
					<v-card-text class="white">
						<v-row>
							<v-col class="report-body">
								<editor
									ref="editor"
									v-model="form.final_report_data"
									api-key="gpl"
									:init="editorInit" />
							</v-col>
						</v-row>
					</v-card-text>
				</v-card>
			</v-sheet>
		</page>
		<v-btn
			elevation="5"
			color="success"
			dark
			fixed
			x-large
			bottom
			right
			class="floating-save-btn justify-start"
			:disabled="! form.isDirty"
			@click="save">
			<v-icon left class="pr-3 pl-1">mdi-content-save</v-icon>
			Save Report
		</v-btn>
		<v-btn
			elevation="5"
			color="warning"
			dark
			fixed
			bottom
			right
			class="floating-top-btn justify-start"
			@click="toTop">
			<v-icon left class="pr-3 pl-1">mdi-arrow-up-bold</v-icon>
			Back to top
		</v-btn>
	</v-form>
</template>

<script>
import { Inertia } from '@inertiajs/inertia'
import api from '@/api'
import Backend from '@/Layouts/Backend'
import Page from '@/Pages/Backend/Components/Page'

import 'tinymce/tinymce'
import 'tinymce/themes/silver'
import 'tinymce/skins/ui/tinymce-5/skin.min.css'
import 'tinymce/icons/default'
import 'tinymce/plugins/advlist'
import 'tinymce/plugins/autolink'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/link'
import 'tinymce/plugins/image'
import 'tinymce/plugins/charmap'
import 'tinymce/plugins/preview'
import 'tinymce/plugins/anchor'
import 'tinymce/plugins/searchreplace'
import 'tinymce/plugins/code'
import 'tinymce/plugins/fullscreen'
import 'tinymce/plugins/insertdatetime'
import 'tinymce/plugins/media'
import 'tinymce/plugins/table'
import 'tinymce/plugins/help'
import 'tinymce/plugins/help/js/i18n/keynav/en'
import 'tinymce/plugins/wordcount'
import 'tinymce/plugins/autoresize'
import 'tinymce/plugins/visualblocks'
import 'tinymce/models/dom'
import Editor from '@tinymce/tinymce-vue'

export default {
	components: { Page, Editor },
	layout: Backend,
	remember: ['form'],
	props: {
		project: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		client: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		report: {
			type: [Array, Object],
			default: () => { return [] },
			required: true
		},
		final: {
			type: [Array, Object],
			default: () => { return [] },
			required: true
		}
	},
	data () {
		return {
			unsavedChangesListener: () => {},
			statusMenu: false,
			statusId: null,
			statuses: [],
			formValid: true,
			dialogEdit: false,

			breadcrumbs: [
				{ route: 'dashboard', label: 'Dashboard' },
				{ route: 'clients.show', params: { client: this.client }, label: this.client.name },
				{ route: 'projects.index', label: 'Projects' },
				{ route: 'projects.show', params: { project: this.project }, label: this.project.name, id: this.project.id },
				{ label: this.report.type.report_type }
			],

			form: this.$inertia.form({
				final_report_data: this.final.final_report_data
			}),

			dialog: false,
			submitted: false,
			saving: false,
			downloading: false,

			editorInit: {
				license_key: 'gpl',
				height: 1000,
				menubar: false,
				selector: '#reportContent',
				content_style: 'p { margin-top: 0 !important; line-height: 1.3 !important; } td { padding: 5px; }',
				plugins: [
					'advlist',
					'autolink',
					'lists',
					'link',
					'image',
					'charmap',
					'preview',
					'anchor',
					'searchreplace',
					'visualblocks',
					'code',
					'fullscreen',
					'insertdatetime',
					'media',
					'table',
					'help',
					'wordcount'
				],
				toolbar:
					'undo redo | formatselect | styles | bold italic backcolor | \
				   alignleft aligncenter alignright alignjustify | \
				   bullist numlist outdent indent | removeformat | help',
				browser_spellcheck: true,
				skin: false,
				content_css: 'oxide',
				visual_table_class: 'mce-item-table'
			}
		}
	},

	created () {
		this.unsavedChangesListener = Inertia.on('before', () => {
			if (this.form.isDirty && (!this.saving && !this.downloading && !this.submitted)) {
				return confirm('Unsaved changes! Are you sure you want to navigate away?')
			}
		})
	},

	beforeDestroy () {
		this.unsavedChangesListener()
	},

	mounted () {
		this.getReportStatuses()
		this.statusId = this.report.status.id
	},

	methods: {
		getReportStatuses () {
			const method = 'GET'
			const data = {}

			const url = this.route('report-statuses.api.index')

			return api({
				method,
				url,
				data
			}).then(response => {
				this.statuses = response.data
			})
		},

		updateStatus () {
			const data = { report_status_id: this.statusId }
			this.$inertia.put(this.route('reports.update.status', this.report), data, {
				only: ['report', 'flash'],
				onFinish: () => this.statusMenu = false,
				preserveState: true,
				preserveScroll: true
			})
		},

		save (download = false) {
			// If the user has not made any changes to the form
			// Download without re-saving
			if (! this.form.isDirty) {
				window.location = this.route('reports.final.download', this.final)
				return
			}

			this.downloading = download
			this.saving = true

			this.form.put(this.route('reports.final.update', this.final), {
				only: ['final', 'report', 'flash'],
				preserveScroll: true,
				preserveState: true,
				onSuccess: () => {
					if (download) {
						// Direct user to direct download link
						window.location = this.route('reports.final.download', this.final)

						// Fake delay to give the browser a chance to save the file
						setTimeout(() => this.downloading = false, 800)
					}

					this.$emit('success')
				},
				onFinish: () => this.saving = false
			})
		},

		regenerate () {
			this.submitted = true

			this.$inertia.get(this.route('reports.finalize', {'report': this.report, 'report_type_id': this.report.report_type_id}), {
				only: ['final', 'report', 'flash'],
				onFinish: () => {
					this.dialog = false
					this.submitted = false
				},
				preserveState: true,
				preserveScroll: true
			})
		},

		toTop () {
			window.scrollTo({ top: 0, behavior: 'smooth' })
			if (this.$refs.editor && this.$refs.editor.editor) {
				this.$refs.editor.editor.getWin().scrollTo(0, 0)
			}
		}
	}
}
</script>

<style scoped>
html {
	box-sizing: border-box;
	font-size: 14px;
	line-height: 20px;
	font-family: Arial, 'sans-serif';
}

*, *:before, *:after {
	box-sizing: inherit;
}

body, h1, h2, h3, h4, h5, h6, p {
	margin: 15px;
	padding: 0;
	font-weight: normal;
}

p {
	margin: 0 0 15px;
}

table, thead, tbody, tr, td, th {
	margin: 0;
	padding: 0;
	border: 0;
}
table {
	width: 100%;
}
/* Start Custom Classes */
h1 {
	line-height: 1.3;
	margin: 0;
}
.logo-new {
	width:265px;
	height:65px
}
.width-one-third {
	width:33%
}
.text-left {
	text-align: left;
}
.text-center {
	text-align: center;
}
.text-right {
	text-align: right;
}
.vertical-middle {
	vertical-align: middle;
}
.vertical-top {
	vertical-align: top;
}
.pl-5 {
	padding-left: 5px;
}
.outer-border {
	border: 4px solid #135B92;
	margin: 15px 0;
	border-radius: 5px;
	border-spacing: 0;
}
.inner-border {
	border: 2px solid #666666;
	margin: 1px;
	border-spacing: 0;
}
.border-bottom-thick {
	border-bottom:2px solid #666666;
	border-spacing: 0;
}
.border-top-thick {
	border-top:2px solid #666666;
	border-spacing: 0;
}
.border-top {
	border-top: 1px solid #666666;
	border-spacing: 0;
}
.border-bottom {
	border-bottom: 1px solid #666666;
	border-spacing: 0;
}
.col-left-label {
	background-color: #91b1e3;
	width:16%;
	padding: 5px 10px 5px 5px;
	border-right: 1px solid #666666;
}
.col-label {
	background-color: #91b1e3;
	padding: 10px;
	border-top: 1px solid #666666;
	border-bottom: 1px solid #666666;
}
.col-left-label-light {
	background-color: #c7d8f1;
	width:16%;
	padding: 5px 10px 5px 5px;
	border-right: 1px solid #666666;
}
.col-data {
	padding: 5px 5px 5px 10px;
}
.col-padding-5 {
	padding: 5px;
}
.col-width-7 {
	width: 7%;
}
.col-width-34 {
	width: 34%;
}
.col-width-26 {
	width: 26%;
}
.col-width-27 {
	width: 27%;
}
.col-width-31 {
	width: 31%;
}
.col-width-84 {
	width: 84%;
}
.col-width-93 {
	width: 93%;
}
.low {
	background-color: #95d44b;
}
.medium {
	background-color: #feff00;
}
.high {
	background-color: #f80003;
}
.font-small {
	font-size: 0.813em;
	line-height: 1.1;
}
</style>
